import { ChakraProvider } from '@chakra-ui/react';
import * as React from 'react';
import SessionProvider from '../session/SessionProvider';

import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { useEffect } from 'react';
import { Layout } from '../components/Layout';
import { theme } from '../constants/_theme';
import * as gtag from '../helpers/gtag';
import '../styles.2.0.css'; // Primary: Chakra Petch font
import '../styles.css'; // Fallback: Old styles

export default function MyApp({
  Component,
  pageProps,
}: {
  Component: React.ComponentType;
  pageProps: Record<string, unknown>;
}): JSX.Element {
  useDevelopmentPageLoader();
  useFlowWidget();
  const shouldLoadCookiebot = process.env.NODE_ENV !== 'development';
  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL?.replace(/\/$/, '');
  const organizationStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Mitt Lager',
    ...(siteUrl && {
      '@id': `${siteUrl}/#organization`,
      url: siteUrl,
      logo: `${siteUrl}/images/logo.png`,
    }),
    email: 'kundservice@mittlager.se',
    telephone: '+46 10 263 6900',
    vatID: 'SE559054179201',
  };

  const router = useRouter();
  useEffect(() => {
    const handleRouteChange = (url) => {
      gtag.pageview(url);
    };
    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events]);

  return (
    <>
      {shouldLoadCookiebot && (
        <Script
          id="Cookiebot"
          src="https://consent.cookiebot.com/uc.js"
          strategy="afterInteractive"
          data-cbid="fd32389e-5c40-4178-b5cf-dd2cc6d0bd87"
        />
      )}
      {process.env.NODE_ENV === 'development' && (
        <Script
          src="https://unpkg.com/nprogress@0.2.0/nprogress.js"
          strategy="afterInteractive"
        />
      )}
      <ChakraProvider theme={theme}>
        <Head>
          {/* <meta name="facebook-domain-verification" content="g744jvxa9s4lsedc6e36kq2thstfp8" /> */}
          <link rel="preconnect" href="https://fonts.gstatic.com" />
          <link rel="preconnect" href="https://www.gstatic.com" />
          <link rel="preconnect" href="https://o437175.ingest.sentry.io" />
          <link rel="preconnect" href="https://relay.telavox.com" />
          <link key="favicon" rel="icon" href="/favicon.ico" />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(organizationStructuredData).replace(
                /</g,
                '\\u003c',
              ),
            }}
          />
        </Head>
        <SessionProvider>
          <Layout {...Object.assign({}, pageProps.sharedPageProps)}>
            <Component {...pageProps} />
          </Layout>
        </SessionProvider>
      </ChakraProvider>
    </>
  );
}

function useDevelopmentPageLoader() {
  const router = useRouter();

  React.useEffect(() => {
    const handleRouteChange = () => {
      window['NProgress'].start();
    };

    const handleRouteChangeComplete = () => {
      window['NProgress'].done();
    };

    if (process.env.NODE_ENV === 'development') {
      router.events.on('routeChangeStart', handleRouteChange);
      router.events.on('routeChangeComplete', handleRouteChangeComplete);
    }

    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
    };
  }, [router.events]);
}

function useFlowWidget() {
  useEffect(() => {
    const onLoad = () => {
      setTimeout(() => {
        const script = document.createElement('script');
        script.src =
          'https://customerwidget.joinflow.com/tvx-customer-widget-app.js?widgetId=' +
          process.env.NEXT_PUBLIC_FLOW_WIDGET_ID;
        script.async = true;
        script.defer = true;
        document.body.appendChild(script);
        document.removeEventListener('load', onLoad);
      }, 4000);
    };
    if (!process.env.NEXT_PUBLIC_FLOW_WIDGET_ID) return;
    window.addEventListener('load', onLoad);
    return () => {
      window.removeEventListener('load', onLoad);
    };
  }, []);
}
